import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { EnvelopeIcon } from '@heroicons/react/24/outline';

import { authService } from '../services/auth';
import { Card } from '../design-system/components/Card';
import { Button } from '../design-system/components/Button';
import { Heading, Text } from '../design-system/components/Typography';
import { Stack } from '../design-system/components/Layout';
import { Input } from '../design-system/components/Input';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { breakpoints } from '../design-system/tokens/breakpoints';
import { hexToRgba } from '../utils/colorUtils';
import { shadows } from '../design-system/tokens/shadows';

const Page = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 73px);
  padding: ${spacing[6]} ${spacing[4]};
  background: ${props => props.theme.colors.surface};
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 480px;
`;

const FormCard = styled(Card)`
  padding: ${spacing[8]};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

const StyledLink = styled(Link)`
  color: ${props => props.theme.colors.icomBlue || props.theme.colors.primary};
  text-decoration: none;
  font-weight: ${typography.fontWeight.semibold};
  font-size: ${typography.fontSize.sm};

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const Footer = styled.div`
  margin-top: ${spacing[6]};
  text-align: center;
  padding-top: ${spacing[6]};
  border-top: 1px solid ${props => props.theme.colors.borderLight};

  ${breakpoints.down.sm} {
    text-align: left;
  }
`;

const SuccessMessage = styled(Card)`
  background: ${props => {
    const ok = hexToRgba(props.theme.colors.success, 0.1);
    return `linear-gradient(135deg, ${ok} 0%, ${hexToRgba(props.theme.colors.success, 0.08)} 100%)`;
  }};
  color: ${props => props.theme.colors.success};
  padding: ${spacing[4]} ${spacing[5]};
  border: 1px solid ${props => hexToRgba(props.theme.colors.success, 0.3)};
  box-shadow: ${props => shadows.colored.md(props.theme.colors.success)};
  border-left: 4px solid ${props => props.theme.colors.success};
`;

const ErrorMessage = styled(Card)`
  background: ${props => {
    const err = hexToRgba(props.theme.colors.error, 0.1);
    return `linear-gradient(135deg, ${err} 0%, ${hexToRgba(props.theme.colors.error, 0.08)} 100%)`;
  }};
  color: ${props => props.theme.colors.error};
  padding: ${spacing[4]} ${spacing[5]};
  border: 1px solid ${props => hexToRgba(props.theme.colors.error, 0.3)};
  box-shadow: ${props => shadows.colored.md(props.theme.colors.error)};
  border-left: 4px solid ${props => props.theme.colors.error};
`;

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmed = email.trim();
    if (!trimmed) {
      setError(t('forgotPassword.emailRequired'));
      return;
    }
    if (!emailPattern.test(trimmed)) {
      setError(t('forgotPassword.emailInvalid'));
      return;
    }

    setIsLoading(true);
    try {
      await authService.forgotPassword(trimmed);
      setSubmitted(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('forgotPassword.requestFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Page>
      <Wrapper>
        <FormCard variant="elevated" accentBorder="top" headerVariant="gradient">
          <Stack direction="column" spacing="md">
            <Heading variant="h2" color="text">
              {t('forgotPassword.title')}
            </Heading>
            <Text color="secondary" size="sm">
              {t('forgotPassword.intro')}
            </Text>

            {submitted ? (
              <SuccessMessage variant="filled">
                <Text color="success" size="sm" weight="medium">
                  {t('forgotPassword.checkEmail')}
                </Text>
              </SuccessMessage>
            ) : (
              <>
                {error && (
                  <ErrorMessage variant="filled">
                    <Text color="error" size="sm" weight="medium">
                      {error}
                    </Text>
                  </ErrorMessage>
                )}

                <Form onSubmit={handleSubmit} noValidate>
                  <Input
                    id="forgot-password-email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    label={t('forgotPassword.email')}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder={t('forgotPassword.emailPlaceholder')}
                    variant="outlined"
                    iconLeft={<EnvelopeIcon />}
                    required
                  />
                  <Button
                    type="submit"
                    variant="gradient"
                    size="lg"
                    fullWidth
                    loading={isLoading}
                    glow
                    ripple
                  >
                    {t('forgotPassword.submit')}
                  </Button>
                </Form>
              </>
            )}

            <Footer>
              <StyledLink to="/login">{t('login.backToSignIn')}</StyledLink>
            </Footer>
          </Stack>
        </FormCard>
      </Wrapper>
    </Page>
  );
};

export default ForgotPassword;
